import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../context/AuthContext'
import { Eye, EyeOff, Mail, Lock, ArrowRight, Loader } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthHeader from '../../components/auth/AuthHeader'
import { vendorService } from '../../services/vendorService'

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false)
  const [focusedField, setFocusedField] = useState(null)

  const { login, loginWithToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'
  const autoLoginData = location.state?.autoLogin ? location.state : null

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const getRegistrationRoute = (userRole) => {
    const roleRoutes = {
      vendor: '/vendor/registration/legal-details',
      company: '/company/registration',
      freelancer: '/freelancer/registration'
    }

    return roleRoutes[userRole?.toLowerCase()] || '/vendor/dashboard'
  }

  const getDashboardRoute = (userRole) => {
    const defaultRoutes = {
      vendor: '/vendor/dashboard',
      company: '/dashboard/company',
      freelancer: '/freelancer/dashboard',
      admin: '/admin/dashboard'
    }
    return defaultRoutes[userRole?.toLowerCase()] || '/dashboard'
  }

  // Auto-login after registration
  const autoLoginAttempted = useRef(false)

  useEffect(() => {
    if (autoLoginData?.autoLogin && autoLoginData?.credentials) {
      // Prevent double invocation
      if (autoLoginAttempted.current) return
      autoLoginAttempted.current = true

      const { email, password, message, token } = autoLoginData.credentials

      // Show success message if provided
      if (message) {
        toast.success(message, { duration: 2000 })
      }

      // Set auto-logging in state
      setIsAutoLoggingIn(true)

      // Auto-login after a short delay to show the loader
      const autoLogin = async () => {
        try {
          // Set form data
          setFormData({ email, password })

          let result;

          if (token) {
            // Use the token directly if available (avoids double login API call)
            result = await loginWithToken(token)
          } else {
            // Fallback to standard login
            result = await login({ email, password })
          }

          if (result.success) {
            // Fetch fresh user data to ensure we have latest status
            let user = result.data
            try {
              const freshUser = await vendorService.getCurrentUser()
              if (freshUser) {
                user = freshUser
              }
            } catch (error) {
              console.warn('Failed to fetch fresh user data, using login response:', error)
              // Continue with user data from login response
            }

            const userRole = user?.role
            const regStatus = user?.hasCompletedRegistration
            const adminReview = user?.admin_review || user?.adminReview
            const userStatus = user?.status
            const bgvStatus = user?.BGV_status || user?.bgv_status

            // Small delay to ensure state is updated
            setTimeout(() => {
              // Admin always goes to admin dashboard
              if (userRole?.toLowerCase() === 'admin') {
                navigate('/admin/dashboard', { replace: true })
                return
              }

              // Check if registration is fully completed (true)
              const isFullyCompleted = regStatus === true || 
                                      regStatus === "true" || 
                                      regStatus === 1 || 
                                      regStatus === '1' ||
                                      String(regStatus).toLowerCase() === 'true'

              if (isFullyCompleted) {
                // Registration completed - check admin review status
                if (adminReview === 'approved' && userStatus === 'active') {
                  // Fully approved - go to dashboard
                  navigate('/vendor/dashboard', { replace: true })
                } else {
                  // Completed but not approved - check BGV status
                  if (bgvStatus === 'uploaded' || bgvStatus === 'completed') {
                    // Documents uploaded - show verification screen (DocumentUploadPage handles this)
                    navigate('/vendor/registration/documents', { replace: true })
                  } else {
                    // BGV pending - show document upload
                    navigate('/vendor/registration/documents', { replace: true })
                  }
                }
              } else if (regStatus === 'vendor-profile' || regStatus === 'vendor_profile') {
                // Step 2 completed - go to Step 3 (Subscription)
                navigate('/vendor/registration/subscription', { replace: true })
              } else if (regStatus === 'vendor-register' || regStatus === 'vendor_register') {
                // Step 1 completed - go to Step 2 (Profile Building)
                navigate('/vendor/registration/profile', { replace: true })
              } else {
                // No registration started or false - go to Step 1 (Legal Details)
                navigate('/vendor/registration/legal-details', { replace: true })
              }
            }, 100)
          } else {
            // If token login failed, try standard login as fallback
            if (token) {
              console.warn("Token login failed, falling back to credentials login")
              const fallbackResult = await login({ email, password })
              if (fallbackResult.success) {
                // handle success logic copied from above, or just reload page to retry?
                // Better to recursive call or just copy logic.
                // For simplicity, let's just fail or rely on user to click login.
                setIsAutoLoggingIn(false)
              } else {
                setIsAutoLoggingIn(false)
              }
            } else {
              setIsAutoLoggingIn(false)
            }
          }
        } catch (error) {
          console.error('Auto-login error:', error)
          setIsAutoLoggingIn(false)
        }
      }

      // Start auto-login after showing the message
      setTimeout(() => {
        autoLogin()
      }, 500)
    }
  }, [autoLoginData, login, loginWithToken, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const result = await login(formData)

      if (result.success) {
        toast.success('Welcome back! Redirecting...')

        // Fetch fresh user data to ensure we have latest status
        let user = result.data
        try {
          const freshUser = await vendorService.getCurrentUser()
          if (freshUser) {
            user = freshUser
          }
        } catch (error) {
          console.warn('Failed to fetch fresh user data, using login response:', error)
          // Continue with user data from login response
        }

        const userRole = user?.role
        const regStatus = user?.hasCompletedRegistration
        const adminReview = user?.admin_review || user?.adminReview
        const userStatus = user?.status
        const bgvStatus = user?.BGV_status || user?.bgv_status

        // Small delay to ensure state is updated
        setTimeout(() => {
          // Admin always goes to admin dashboard
          if (userRole?.toLowerCase() === 'admin') {
            navigate('/admin/dashboard', { replace: true })
            return
          }

          // Check if registration is fully completed (true)
          const isFullyCompleted = regStatus === true || 
                                  regStatus === "true" || 
                                  regStatus === 1 || 
                                  regStatus === '1' ||
                                  String(regStatus).toLowerCase() === 'true'

          if (isFullyCompleted) {
            // Registration completed - check admin review status
            if (adminReview === 'approved' && userStatus === 'active') {
              // Fully approved - go to dashboard
              navigate('/vendor/dashboard', { replace: true })
            } else {
              // Completed but not approved - check BGV status
              if (bgvStatus === 'uploaded' || bgvStatus === 'completed') {
                // Documents uploaded - show verification screen (DocumentUploadPage handles this)
                navigate('/vendor/registration/documents', { replace: true })
              } else {
                // BGV pending - show document upload
                navigate('/vendor/registration/documents', { replace: true })
              }
            }
          } else if (regStatus === 'vendor-profile' || regStatus === 'vendor_profile') {
            // Step 2 completed - go to Step 3 (Subscription)
            navigate('/vendor/registration/subscription', { replace: true })
          } else if (regStatus === 'vendor-register' || regStatus === 'vendor_register') {
            // Step 1 completed - go to Step 2 (Profile Building)
            navigate('/vendor/registration/profile', { replace: true })
          } else {
            // No registration started or false - go to Step 1 (Legal Details)
            navigate('/vendor/registration/legal-details', { replace: true })
          }
        }, 100)
      }
      else {
        toast.error(result.error || 'Invalid credentials. Please try again.')
      }
    } catch (error) {
      console.error('Login error:', error)
      toast.error(error.message || 'Connection error. Please check your network.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 relative">
      <AuthHeader />
      
      {/* Auto-login Loader Overlay */}
      {isAutoLoggingIn && (
        <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center space-y-4"
          >
            <Loader className="w-10 h-10 text-primary-600 animate-spin" />
            <div className="text-center">
              <p className="text-base font-semibold text-secondary-900 dark:text-secondary-100 mb-1">
                Registration Successful!
              </p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Logging you in...
              </p>
            </div>
          </motion.div>
        </div>
      )}

      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] p-4 py-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card w-full max-w-lg p-5 md:p-6 shadow-glow"
        >
          {/* Header */}
          <div className="text-center mb-5">
            <h1 className="text-2xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-1.5 tracking-tight">
              Welcome Back
            </h1>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 max-w-sm mx-auto">
              Sign in to your account to continue
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Email Field */}
            <div className="space-y-1">
              <label htmlFor="email" className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className="input pl-9 h-9 text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                  Password
                </label>
                <Link
                  to="/auth/forgot-password"
                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="input pl-9 pr-9 h-9 text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-start gap-2 pt-0.5">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="w-3.5 h-3.5 mt-0.5 border-2 border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 bg-white dark:bg-secondary-800 text-primary-600 cursor-pointer"
              />
              <label htmlFor="remember-me" className="text-xs text-secondary-600 dark:text-secondary-400 leading-relaxed cursor-pointer">
                Remember me for 30 days
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full h-10 text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              {isLoading ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 pt-4 border-t border-secondary-200 dark:border-secondary-700 text-center">
            <p className="text-xs text-secondary-600 dark:text-secondary-400">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold underline-offset-2 hover:underline transition-colors"
              >
                Sign up for free
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default LoginPage